import {
  GetForm_vm_form_formItems as FormItemType,
  GetForm_vm_form_formItems_VOLUNTEER_FormItemFieldType as FieldFormItemType,
} from '../__generated__/GetForm';

export type FormItemsMapById = { [key: string]: FormItemType };
export type FormItemsByTrigger = {
  [key: string]: {
    formItemId: string;
    triggerItemIds: string[];
    taskFieldId: string;
    optionType: string;
  };
};

export type FormItemByTriggerOption = {
  [key: string]: string;
};

export type FormItemsDeserialize = {
  allIds: string[];
  byId: FormItemsMapById;
  byTrigger: FormItemsByTrigger;
  byTriggerOption: FormItemByTriggerOption;
};

export function deserializeFormItems(formItems?: FormItemType[]): FormItemsDeserialize {
  if (!formItems || formItems.length === 0) {
    return {
      allIds: [],
      byId: {},
      byTrigger: {},
      byTriggerOption: {},
    };
  }

  // Build byTriggerOption first to identify which items are triggered
  const byTriggerOption = formItems.reduce((acc, formItem) => {
    const triggerOption: any = (formItem as any).triggerItem?.trigger?.triggerOption;

    if (triggerOption) {
      const optionId =
        triggerOption.sourceOptionValue ||
        triggerOption.quizTaskFieldOptionId ||
        triggerOption.dropdownFieldOptionId ||
        triggerOption.checkboxFieldOptionId ||
        triggerOption.multipleChoiceFieldOptionId;
      return {
        ...acc,
        [formItem.formItemId]: optionId,
      };
    }

    return acc;
  }, {} as FormItemByTriggerOption);

  // allIds only includes top-level items (not triggered items)
  const allIds = formItems
    .filter((formItem) => !byTriggerOption[formItem.formItemId])
    .map((formItem) => formItem.formItemId);

  return {
    allIds,
    byId: formItems.reduce(
      (acc, formItem) => ({
        ...acc,
        [formItem.formItemId]: formItem,
      }),
      {} as FormItemsMapById
    ),
    byTrigger: formItems.reduce((acc, formItem) => {
      if (formItem.__typename !== 'VOLUNTEER_FormItemFieldType') {
        return acc;
      }

      const fieldItem = formItem as FieldFormItemType;
      let newTriggerMap = {} as FormItemsByTrigger;

      if (fieldItem.field.__typename === 'VOLUNTEER_FormCheckboxTaskFieldType') {
        const options = fieldItem.field.checkboxOptions || [];
        newTriggerMap = options.reduce(
          (triggerMap, checkboxOption) => ({
            ...triggerMap,
            [checkboxOption.checkboxFieldOptionId]: {
              triggerItemIds: checkboxOption.trigger?.items.map((item) => item.item?.formItemId).filter(Boolean) || [],
              formItemId: formItem.formItemId,
              taskFieldId: fieldItem.field.taskFieldId,
              optionType: 'checkbox',
            },
          }),
          {}
        );
      }

      if (fieldItem.field.__typename === 'VOLUNTEER_FormDropdownTaskFieldType') {
        const options = fieldItem.field.dropdownOptions || [];
        newTriggerMap = options.reduce(
          (triggerMap, dropdownOption) => ({
            ...triggerMap,
            [dropdownOption.dropdownFieldOptionId]: {
              triggerItemIds: dropdownOption.trigger?.items.map((item) => item.item?.formItemId).filter(Boolean) || [],
              formItemId: formItem.formItemId,
              taskFieldId: fieldItem.field.taskFieldId,
              optionType: 'dropdown',
            },
          }),
          {}
        );
      }

      if (fieldItem.field.__typename === 'VOLUNTEER_FormMultipleChoiceTaskFieldType') {
        const options = fieldItem.field.multipleChoiceOptions || [];
        newTriggerMap = options.reduce(
          (triggerMap, multipleChoiceOption) => ({
            ...triggerMap,
            [multipleChoiceOption.multipleChoiceFieldOptionId]: {
              triggerItemIds: multipleChoiceOption.trigger?.items.map((item) => item.item?.formItemId).filter(Boolean) || [],
              formItemId: formItem.formItemId,
              taskFieldId: fieldItem.field.taskFieldId,
              optionType: 'multipleChoice',
            },
          }),
          {}
        );
      }

      if (fieldItem.field.__typename === 'VOLUNTEER_FormQuizTaskFieldType') {
        const options = fieldItem.field.quizTaskFieldOptions || [];
        newTriggerMap = options.reduce(
          (triggerMap, quizOption) => ({
            ...triggerMap,
            [quizOption.quizTaskFieldOptionId]: {
              triggerItemIds: quizOption.trigger?.items.map((item) => item.item?.formItemId).filter(Boolean) || [],
              formItemId: formItem.formItemId,
              taskFieldId: fieldItem.field.taskFieldId,
              optionType: 'quiz',
            },
          }),
          {}
        );
      }

      if (fieldItem.field.__typename === 'VOLUNTEER_CrossTaskDynamicTaskFieldType') {
        newTriggerMap = (fieldItem.field.crossTaskDynamicOptions || []).reduce(
          (triggerMap, crossTaskOption) => ({
            ...triggerMap,
            [crossTaskOption.sourceOptionValue]: {
              triggerItemIds: crossTaskOption.trigger?.items?.map((item) => item.item?.formItemId).filter(Boolean) || [],
              formItemId: formItem.formItemId,
              taskFieldId: fieldItem.field.taskFieldId,
              optionType: 'crossTaskDynamic',
            },
          }),
          {}
        );
      }

      return { ...acc, ...newTriggerMap };
    }, {} as FormItemsByTrigger),
    byTriggerOption,
  };
}
